// Menu "Rampa de lançamentos": lista de produtos já lançados em acompanhamento
// mensal (12 meses) para a gestão. Busca por nome/marca/código e cadastro manual
// (o automático acontece ao concluir a última etapa do projeto).
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { canFillRamps } from '../lib/rbac';
import { loadBrands, createBrand, type BrandRow } from '../lib/data';
import { uploadRampImage } from '../lib/photos';
import { lookupProductByCodigo, syncProductPhoto } from '../lib/productLink';
import { ultimoMesLiberado } from '../shared/fechamento';
import {
  loadRamps,
  createRamp,
  updateRamp,
  loadRampEntries,
  rampMonthsList,
  monthLabel,
  type LaunchRamp,
  type RampStatus,
} from '../lib/launchRamps';
import { legendaPco, calcularAncora, ROTULO_ANCORA_PCT, ROTULO_ANCORA_PCT_EN } from '../shared/ancoragem';
import { vendaUnLojaSemana, ROTULO_UN_LOJA_SEMANA, ROTULO_UN_LOJA_SEMANA_EN } from '../shared/rampaIndicadores';
import { buildListReportHtml, printReport, prepararFotos, EN_LABELS, type ReportSpec } from '../lib/report';

const brl = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const brl2 = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const numf = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
const dec1 = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const pctf = (n: number | null | undefined) =>
  n == null ? '—' : `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

export function Rampa() {
  const { currentOrgId, grants, userId, fullName } = useAuth();
  const nav = useNavigate();
  const canFill = grants ? canFillRamps(grants) : false;
  const [items, setItems] = useState<LaunchRamp[]>([]);
  const [loading, setLoading] = useState(true);
  const [pend, setPend] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [brandFilter, setBrandFilter] = useState('todas');
  const [statusFilter, setStatusFilter] = useState<'acompanhando' | 'todos' | RampStatus>('acompanhando');
  const [adding, setAdding] = useState(false);

  async function reload() {
    if (!currentOrgId) return;
    setLoading(true);
    const list = await loadRamps(currentOrgId);
    setItems(list);
    setLoading(false);
    // Cobra o último mês JÁ FECHADO (liberado a partir do dia 10 do mês seguinte), não o mês em andamento.
    const thisMonth = ultimoMesLiberado();
    const missing = new Set<string>();
    await Promise.all(
      list
        .filter((d) => d.status === 'acompanhando')
        .map(async (d) => {
          const anchor = d.salesStartDate ?? d.createdAt;
          const months = rampMonthsList(anchor, d.planMonths);
          if (!months.includes(thisMonth)) return;
          const entries = await loadRampEntries(d.id);
          // Só conta como preenchido se tiver números (uma linha só com o preço do âncora não vale).
          if (!entries.some((e) => e.refMonth === thisMonth && (e.rob != null || e.volume != null || e.pca != null))) missing.add(d.id);
        }),
    );
    setPend(missing);
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId]);

  const brandOptions = useMemo(() => {
    const s = new Set<string>();
    items.forEach((i) => i.brandName && s.add(i.brandName));
    return [...s].sort();
  }, [items]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((i) => {
      if (brandFilter !== 'todas' && i.brandName !== brandFilter) return false;
      if (statusFilter === 'acompanhando' && i.status !== 'acompanhando') return false;
      if (statusFilter !== 'acompanhando' && statusFilter !== 'todos' && i.status !== statusFilter) return false;
      if (term && !`${i.name} ${i.brandName ?? ''} ${i.codigo ?? ''}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [items, q, brandFilter, statusFilter]);

  const [gerando, setGerando] = useState(false);
  // Ancoragem de preço no relatório: desligada por padrão (os preços ainda são de teste).
  const [incluirAncora, setIncluirAncora] = useState(false);
  async function relatorioGeral(lang: 'pt' | 'en') {
    if (!filtered.length) return;
    setGerando(true);
    try {
      const EN = lang === 'en';
      const enLbl = {
        rob: 'Gross revenue (ROB)', volume: 'Volume', pca: 'PCA',
        mc: 'Contribution margin (%)', devolucao: 'Returns (%)', preco: 'Average price',
      };
      const specs: ReportSpec[] = [];
      for (const r of filtered) {
        const anchor = r.salesStartDate ?? r.createdAt;
        const months = rampMonthsList(anchor, r.planMonths);
        const entries = await loadRampEntries(r.id);
        const byM = new Map(entries.map((e) => [e.refMonth, e]));
        const pts = (pick: (e: typeof entries[number]) => number | null) =>
          months.map((m) => ({ label: monthLabel(m), value: byM.has(m) ? pick(byM.get(m)!) : null }));
        const venda = r.salesStartDate ? new Date(r.salesStartDate + 'T00:00:00').toLocaleDateString(EN ? 'en-GB' : 'pt-BR') : '—';
        specs.push({
          title: r.name,
          brand: r.brandName,
          subtitle: `${r.brandName ?? ''}${r.codigo ? ` · ${r.codigo}` : ''}`,
          tagline: EN ? 'Launch ramp' : 'Rampa de lançamento',
          photoUrl: r.photoUrl,
          header: EN
            ? [{ label: 'Brand', value: r.brandName ?? '—' }, { label: 'Code', value: r.codigo ?? '—' }, { label: 'Size', value: r.gramatura ?? '—' }, { label: 'Sales start', value: venda }]
            : [{ label: 'Marca', value: r.brandName ?? '—' }, { label: 'Código', value: r.codigo ?? '—' }, { label: 'Gramatura', value: r.gramatura ?? '—' }, { label: 'Início das vendas', value: venda }],
          indicators: [
            { title: EN ? enLbl.rob : 'ROB', meta: r.metaRob, points: pts((e) => e.rob), fmt: brl },
            { title: EN ? enLbl.volume : 'Volume', meta: r.metaVolume, points: pts((e) => e.volume), fmt: numf },
            { title: 'PCA', meta: r.metaPca, points: pts((e) => e.pca), fmt: numf },
            { title: EN ? enLbl.mc : 'Margem de contribuição (%)', meta: r.metaMc, points: pts((e) => e.mc), fmt: pctf },
            { title: EN ? enLbl.devolucao : '% Devolução', meta: r.metaDevolucao, points: pts((e) => e.devolucao), fmt: pctf, lowerIsBetter: true },
            { title: EN ? enLbl.preco : 'Preço médio', meta: r.metaPrecoMedio, points: pts((e) => e.precoMedio), fmt: brl2 },
            { title: EN ? ROTULO_UN_LOJA_SEMANA_EN : ROTULO_UN_LOJA_SEMANA, meta: null, points: pts((e) => vendaUnLojaSemana(e.volume, e.pca, r.gramatura)), fmt: dec1 },
            ...(incluirAncora && r.ancoraNome && r.ancoraElasticidade != null
              ? [{
                  title: EN ? ROTULO_ANCORA_PCT_EN : ROTULO_ANCORA_PCT,
                  meta: r.ancoraElasticidade,
                  points: pts((e) => calcularAncora({ nome: r.ancoraNome, gramatura: r.ancoraGramatura, elasticidade: r.ancoraElasticidade, base: r.ancoraBase }, r.gramatura, e.precoMedio, e.ancoraPreco)?.pct ?? null),
                  fmt: pctf,
                }]
              : []),
          ],
          sections: incluirAncora && r.ancoraNome && r.ancoraElasticidade != null
            ? [{ title: EN ? 'Legend' : 'Legenda', items: [{ head: 'PCO', body: legendaPco(EN) }] }]
            : undefined,
        });
      }
      const html = buildListReportHtml({
        title: EN ? 'Launch ramp — performance report' : 'Rampa de lançamentos — relatório geral',
        subtitle: `${filtered.length} ${EN ? 'items' : 'itens'} · ${new Date().toLocaleDateString(EN ? 'en-GB' : 'pt-BR')}`,
        items: await prepararFotos(specs),
        labels: EN ? EN_LABELS : undefined,
        footerNote: EN ? 'New Product Workflow — Trevo Lácteos' : 'Workflow de Novos Produtos — Trevo Lácteos',
      }, true);
      printReport(html, EN ? 'launch-ramp-geral-en' : 'rampa-geral-pt');
    } finally {
      setGerando(false);
    }
  }

  return (
    <>
      <div className="topbar">
        <div>
          <div className="muted" style={{ fontSize: 12 }}>Pós-lançamento</div>
          <h1>Rampa de lançamentos</h1>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label className="muted" style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }} title="Inclui o indicador de preço vs. âncora, as colunas de PCO e a legenda nos relatórios">
            <input type="checkbox" checked={incluirAncora} onChange={(e) => setIncluirAncora(e.target.checked)} style={{ minHeight: 0, width: 'auto' }} /> incluir ancoragem de preço
          </label>
          <button className="btn-ghost" disabled={gerando || !filtered.length} onClick={() => relatorioGeral('pt')}>
            {gerando ? 'Gerando…' : '📄 Geral PDF (PT)'}
          </button>
          <button className="btn-ghost" disabled={gerando || !filtered.length} onClick={() => relatorioGeral('en')}>
            📄 Overview PDF (EN)
          </button>
          {canFill && (
            <button className="btn-primary" onClick={() => setAdding(true)}>＋ Novo lançamento</button>
          )}
        </div>
      </div>

      <div className="content">
        <p className="muted" style={{ marginTop: -4 }}>
          Acompanhamento mensal (12 meses) dos produtos lançados, com metas da capa. Ao concluir a
          última etapa de um projeto, ele entra aqui automaticamente. Marketing e Comercial preenchem.
        </p>

        {pend.size > 0 && (
          <div className="card card-pad" style={{ borderLeft: '4px solid #d08700' }}>
            ⏰ <b>{pend.size}</b> lançamento(s) sem o preenchimento de <b>{monthLabel(ultimoMesLiberado())}</b> (mês já fechado).
            O mês em andamento só é liberado após o fechamento, por volta do dia 10 do mês seguinte.
          </div>
        )}

        <div className="disc-toolbar">
          <input className="disc-search" placeholder="Buscar por nome, marca ou código…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
            <option value="todas">Todas as marcas</option>
            {brandOptions.map((b) => (<option key={b} value={b}>{b}</option>))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
            <option value="acompanhando">Em acompanhamento</option>
            <option value="todos">Todos</option>
            <option value="encerrado">Encerrados</option>
          </select>
        </div>

        {loading ? (
          <p className="muted">Carregando…</p>
        ) : filtered.length === 0 ? (
          <p className="muted">Nenhum lançamento encontrado.</p>
        ) : (
          <div className="proj-grid">
            {filtered.map((d) => {
              const venda = d.salesStartDate ? new Date(d.salesStartDate + 'T00:00:00').toLocaleDateString('pt-BR') : '—';
              return (
                <button
                  className={`proj-card ${d.status !== 'acompanhando' ? 'proj-inativo' : ''}`}
                  key={d.id}
                  onClick={() => nav(`/rampa/${d.id}`)}
                >
                  <div className="proj-card-top">
                    <span className={`seal ${d.status === 'acompanhando' ? 'pend' : 'ok'}`}>
                      {d.status === 'acompanhando' ? 'Acompanhando' : 'Encerrado'}
                    </span>
                    {pend.has(d.id) && <span className="disc-pend-chip">⏰ falta lançar</span>}
                  </div>
                  <div className="proj-idrow">
                    {d.photoUrl ? (
                      <img className="proj-thumb" src={d.photoUrl} alt="" loading="lazy" />
                    ) : (
                      <div className="proj-thumb ph" aria-hidden="true">🚀</div>
                    )}
                    <div className="proj-idtext">
                      <div className="proj-brand">
                        {d.brandName ?? 'sem marca'}
                        {d.codigo ? ` – ${d.codigo}` : ''}
                      </div>
                      <div className="proj-name">{d.name}{d.gramatura ? ` · ${d.gramatura}` : ''}</div>
                    </div>
                  </div>
                  <div className="proj-next">
                    <span className="proj-next-lbl">Início vendas</span>
                    <span className="proj-next-txt">{venda} · {d.planMonths} meses</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {adding && currentOrgId && (
        <NovoLancamento
          orgId={currentOrgId}
          userId={userId}
          fullName={fullName}
          onClose={() => setAdding(false)}
          onCreated={(id) => { setAdding(false); nav(`/rampa/${id}`); }}
        />
      )}
    </>
  );
}

function NovoLancamento({
  orgId, userId, fullName, onClose, onCreated,
}: {
  orgId: string;
  userId: string | null;
  fullName: string | null;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [brandId, setBrandId] = useState('');
  const [newBrand, setNewBrand] = useState('');
  const [name, setName] = useState('');
  const [gramatura, setGramatura] = useState('');
  const [codigo, setCodigo] = useState('');
  const [salesStart, setSalesStart] = useState('');
  const [planMonths, setPlanMonths] = useState(12);
  const [mRob, setMRob] = useState('');
  const [mVol, setMVol] = useState('');
  const [mPca, setMPca] = useState('');
  const [mMc, setMMc] = useState('');
  const [mDev, setMDev] = useState('');
  const [mPreco, setMPreco] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [linkedPhoto, setLinkedPhoto] = useState<string | null>(null);
  const [linkInfo, setLinkInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    loadBrands(orgId).then((bs) => { setBrands(bs); setBrandId(bs[0]?.id ?? '__new__'); });
  }, [orgId]);

  const creatingBrand = brandId === '__new__';
  const numOrNull = (v: string) => (v.trim() === '' ? null : Number(v));

  // Mesmo produto na Saída de linha (por código): carrega nome, marca, gramatura e foto.
  async function onCodigoBlur() {
    const found = await lookupProductByCodigo(orgId, codigo);
    if (!found) { setLinkInfo(null); return; }
    if (!name.trim()) setName(found.name);
    if (found.gramatura && !gramatura.trim()) setGramatura(found.gramatura);
    if (found.brandId && brands.some((b) => b.id === found.brandId)) setBrandId(found.brandId);
    if (found.photoUrl) setLinkedPhoto(found.photoUrl);
    const origem = found.from === 'saida' ? 'Saída de linha' : 'Rampa';
    setLinkInfo(`Produto encontrado em ${origem} — dados${found.photoUrl ? ' e foto' : ''} carregados.`);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) return setErr('Dê um nome ao produto.');
    setBusy(true);
    try {
      let finalBrandId: string | null = brandId === '__new__' ? null : brandId;
      if (creatingBrand) {
        const nb = newBrand.trim();
        if (!nb) { setBusy(false); return setErr('Informe o nome da nova marca.'); }
        const { error, brand } = await createBrand(orgId, nb);
        if (error || !brand) { setBusy(false); return setErr(error ?? 'Não foi possível criar a marca.'); }
        finalBrandId = brand.id;
      }
      const { error, id } = await createRamp({
        orgId,
        brandId: finalBrandId,
        name: name.trim(),
        gramatura: gramatura.trim() || null,
        codigo: codigo.trim() || null,
        salesStartDate: salesStart || null,
        planMonths: planMonths || 12,
        metaRob: numOrNull(mRob),
        metaVolume: numOrNull(mVol),
        metaPca: numOrNull(mPca),
        metaMc: numOrNull(mMc),
        metaDevolucao: numOrNull(mDev),
        metaPrecoMedio: numOrNull(mPreco),
        createdById: userId,
        createdByName: fullName,
      });
      if (error || !id) { setBusy(false); return setErr(error ?? 'Não foi possível criar.'); }
      if (file) {
        const up = await uploadRampImage(orgId, id, file);
        if (up.url) {
          await updateRamp(id, { photo_url: up.url });
          await syncProductPhoto(orgId, codigo.trim() || null, up.url);
        }
      } else if (linkedPhoto) {
        // Reaproveita a foto do mesmo produto na Saída de linha.
        await updateRamp(id, { photo_url: linkedPhoto });
      }
      onCreated(id);
    } catch (e) {
      setBusy(false);
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal card" onClick={(e) => e.stopPropagation()}>
        <div className="section-title">
          <h2>Novo lançamento</h2>
          <button className="btn-ghost btn-sm" onClick={onClose}>Fechar</button>
        </div>
        <form onSubmit={submit} className="modal-body">
          <label className="field">
            <span>Nome completo do produto</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Iogurte Grego Apreciare Morango 480g" autoFocus />
          </label>
          <div className="capa-grid">
            <label className="field">
              <span>Marca</span>
              <select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
                {brands.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
                <option value="__new__">＋ Nova marca…</option>
              </select>
            </label>
            <label className="field"><span>Gramatura</span><input value={gramatura} onChange={(e) => setGramatura(e.target.value)} placeholder="Ex.: 480g" /></label>
            <label className="field"><span>Código</span><input value={codigo} onChange={(e) => setCodigo(e.target.value)} onBlur={onCodigoBlur} placeholder="Ex.: 61000408" /></label>
          </div>
          {linkInfo && <p className="muted" style={{ fontSize: 12, margin: '-4px 2px 2px', color: 'var(--brand-green)' }}>🔗 {linkInfo}</p>}
          {creatingBrand && (
            <label className="field"><span>Nome da nova marca</span><input value={newBrand} onChange={(e) => setNewBrand(e.target.value)} /></label>
          )}
          <div className="capa-grid">
            <label className="field"><span>Data prevista de início das vendas</span><input type="date" value={salesStart} onChange={(e) => setSalesStart(e.target.value)} /></label>
            <label className="field"><span>Meses de acompanhamento</span><input type="number" min={1} value={planMonths} onChange={(e) => setPlanMonths(Number(e.target.value))} /></label>
          </div>

          <h3 className="capa-sub">Metas (da capa)</h3>
          <div className="capa-grid">
            <label className="field"><span>Meta ROB (R$)</span><input type="number" value={mRob} onChange={(e) => setMRob(e.target.value)} /></label>
            <label className="field"><span>Meta Volume</span><input type="number" value={mVol} onChange={(e) => setMVol(e.target.value)} /></label>
            <label className="field"><span>Meta PCA</span><input type="number" value={mPca} onChange={(e) => setMPca(e.target.value)} /></label>
            <label className="field"><span>Meta Margem contrib. (%)</span><input type="number" value={mMc} onChange={(e) => setMMc(e.target.value)} /></label>
            <label className="field"><span>Meta % Devolução</span><input type="number" step="0.1" value={mDev} onChange={(e) => setMDev(e.target.value)} /></label>
            <label className="field"><span>Meta Preço médio (R$)</span><input type="number" step="0.01" value={mPreco} onChange={(e) => setMPreco(e.target.value)} /></label>
          </div>

          <label className="field"><span>Imagem do produto</span><input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></label>

          {err && <p className="err">{err}</p>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="btn-primary" disabled={busy}>{busy ? 'Salvando…' : 'Cadastrar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
